import ApplicationController from "./application_controller";
import EditorJS, { OutputData } from "@editorjs/editorjs";

import Header from "@editorjs/header";
import RawTool from "@editorjs/raw";
import ImageTool from "@editorjs/image";
import List from "@editorjs/list";
import Embed from "@editorjs/embed";
import Quote from "@editorjs/quote";
import Table from "@editorjs/table";
import editorjsColumns from "@calumk/editorjs-columns";
import LinkAutocomplete from "@editorjs/link-autocomplete";
import AttachesTool from "@editorjs/attaches";
import ButtonTool from "../tools/button_tool";
import Underline from "@editorjs/underline";
import Strikethrough from "@sotaproject/strikethrough";
import UppercaseTool from "../tools/uppercase_tool";
import TextColorTool from "../tools/text_color_tool";
import AlignmentTune from "../tools/alignment_tune";
import ClearFormattingTune from "../tools/clear_formatting_tune";

interface UploadResponse {
  success: number;
  file: {
    url: string;
  };
}

export default class EditorController extends ApplicationController {
  static targets = ["editor", "editorField"];
  declare editor: EditorJS;
  declare editorTarget: HTMLElement;
  declare editorFieldTarget: HTMLInputElement;

  commonTunes = {
    textAlignment: {
       
      class: AlignmentTune,
      config: {
        default: "center"
      }
    },
    clearFormatting: {
       
      class: ClearFormattingTune
    }
  };

  commonTools = {
    paragraph: {
      tunes: ['textAlignment', 'clearFormatting']
    },
    header: {
      class: Header,
      shortcut: "CMD+SHIFT+H",
      tunes: ['textAlignment', 'clearFormatting']
    },
    link: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      class: LinkAutocomplete,
      config: {
        endpoint: "/admin/editor/autocomplete.json",
        queryParam: "search",
      },
    },
    list: {
      class: List,
      inlineToolbar: true,
      tunes: ['textAlignment', 'clearFormatting'],
      config: {
        defaultStyle: "unordered",
      },
    },
    image: {
      class: ImageTool,
      tunes: [],
      config: {
        uploader: {
          async uploadByFile(file: File): Promise<UploadResponse> {
            const csrfToken = document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content");

            if (!csrfToken) {
              throw new Error("CSRF token not found");
            }

            const formData = new FormData();
            formData.append("image", file);

            const response = await fetch("/admin/editor/images", {
              method: "POST",
              headers: {
                "X-CSRF-Token": csrfToken,
              },
              body: formData,
            });

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(
                `HTTP upload error! Status: ${response.status} - ${errorText}`,
              );
            }

            const result = (await response.json()) as UploadResponse;
            return result;
          },
          async uploadByUrl(url: string): Promise<UploadResponse> {
            const csrfToken = document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content");

            if (!csrfToken) {
              throw new Error("CSRF token not found");
            }

            const formData = new FormData();
            formData.append("image_url", url);

            const response = await fetch("/admin/editor/images", {
              method: "POST",
              headers: {
                "X-CSRF-Token": csrfToken,
              },
              body: formData,
            });

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(
                `HTTP upload error! Status: ${response.status} - ${errorText}`,
              );
            }

            const result = (await response.json()) as UploadResponse;
            return result;
          },
        },
      },
    },

    attaches: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      class: AttachesTool,
      config: {
        uploader: {
          async uploadByFile(file: File): Promise<UploadResponse> {
            console.log(`Uploading file ${file.name}`);
            const csrfToken = document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content");

            if (!csrfToken) {
              throw new Error("CSRF token not found");
            }

            const formData = new FormData();
            formData.append("file", file);

            console.log("Making request to /admin/editor/files");
            const response = await fetch("/admin/editor/files", {
              method: "POST",
              headers: {
                "X-CSRF-Token": csrfToken,
              },
              body: formData,
            });

            console.log(`File upload response status: ${response.status}`);

            if (!response.ok) {
              const errorText = await response.text();
              console.error(`File upload error response: ${errorText}`);
              throw new Error(
                `HTTP upload error! Status: ${response.status} - ${errorText}`,
              );
            }

            const result = (await response.json()) as UploadResponse;
            console.log("File upload successful:", result);
            return result;
          },
        },
      },
    },
    embed: { class: Embed },
    quote: {
      class: Quote,
      tunes: ['textAlignment', 'clearFormatting']
    },
    table: Table,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    raw: { class: RawTool },
    button: {
      class: ButtonTool,
    },
    underline: {
      class: Underline,
    },
    strikethrough: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      class: Strikethrough,
    },
    uppercase: {
      class: UppercaseTool,
    },
    textColor: {
      class: TextColorTool,
    },
  };

  connect() {
    super.connect();

    this.editor = new EditorJS({
      holder: this.editorTarget,
      tools: {
        ...this.commonTools,
        ...this.commonTunes,
        columns: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          class: editorjsColumns,
          config: {
            tools: {
              ...this.commonTools,
              ...this.commonTunes,
            },
            EditorJsLibrary: EditorJS, //ref EditorJS - This means only one global thing
          },
        },
      },
      tunes: Object.keys(this.commonTunes),
      placeholder: "Build rich content here.",
      onChange: () => {
        void this.save();
        return;
      },
    });
    void this.setup();
  }

  async setup() {
    await this.editor.isReady;
    await this.load();
  }

  async save() {
    try {
      const outputData = await this.editor.save();
      this.editorFieldTarget.value = JSON.stringify(outputData);
    } catch (error) {
      console.error("Failed to save editor data:", error);
    }
  }

  async load() {
    try {
      const value = this.editorFieldTarget.value?.trim();
      let data: OutputData;

      if (!value || value === "") {
        // Initialize with empty editor data if no value
        data = {
          time: Date.now(),
          blocks: [],
          version: "2.31.0",
        };
      } else {
        try {
          data = <OutputData>JSON.parse(value);
          // Validate that the parsed data has the expected structure
          if (
            !data ||
            typeof data !== "object" ||
            !Array.isArray(data.blocks)
          ) {
            throw new Error("Invalid editor data structure");
          }
        } catch (parseError) {
          console.warn(
            "Failed to parse editor data, initializing with empty editor:",
            parseError,
          );
          data = {
            time: Date.now(),
            blocks: [],
            version: "2.31.0",
          };
        }
      }

      await this.editor.render(data);
    } catch (error) {
      console.error("Failed to load editor data:", error);
      // Initialize with empty editor data if rendering fails
      await this.editor.render({
        time: Date.now(),
        blocks: [],
        version: "2.31.0",
      });
    }
  }

  disconnect() {
    // EditorJS only exposes destroy() once it has finished initializing; guard
    // it so a disconnect mid-init (fast Turbo navigation) doesn't throw.
    this.editor?.destroy?.();
    super.disconnect();
  }
}
